import { toast } from "react-toastify";
import type { PostFormState } from "shared/types/post";
import type { AccountType } from "shared/types/accounts";
import type { Platform } from "features/post/format-post";
import {
  getLocalStoragePostFormDrafts,
  setLocalStoragePostFormDrafts,
  clearLocalStoragePostFormDrafts,
} from "libs/storage/adapters";

// Constants
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const SAVE_DEBOUNCE_MS = 1000; // Minimum 1 second between saves

// Singleton timestamp tracking for debouncing
let lastSaveTimestamp = 0;

/**
 * Resets the debouncing state
 * Should be called when the bulk modal closes to prevent race conditions
 */
export function resetDebouncingState(): void {
  lastSaveTimestamp = 0;
}

/**
 * Metadata for draft restoration UI
 */
export interface DraftMetadata {
  postId: string;
  accountNames: string[];
  platformNames: string[];
  requestType: "create" | "update";
  caption?: string; // First 50 chars for preview
  mediaCount: number;
  timestamp: number;
}

/**
 * Individual post form draft with metadata
 */
export interface PostFormDraft {
  id: string; // post._id
  timestamp: number; // Date.now() for TTL tracking
  formState: Partial<PostFormState>; // Serializable subset
  metadata: DraftMetadata; // For restoration UI
}

/**
 * Collection of all drafts with versioning
 */
export interface DraftsCollection {
  version: 1; // For future schema migrations
  userId?: string; // User ID to prevent cross-user contamination
  drafts: PostFormDraft[];
  lastSaved: number; // Global timestamp
}

/**
 * Serializes a PostFormState into a storable JSON-compatible format
 * Excludes non-serializable data and runtime-only flags
 */
export function serializeFormState(
  form: PostFormState
): Partial<PostFormState> {
  if (!form.post?._id) {
    throw new Error("Cannot serialize form without post._id");
  }

  return {
    post: form.post,
    platformFields: form.platformFields,
    // Serialize accounts as IDs only - will rehydrate from Redux on restore
    accounts: form.accounts?.map((acc) => ({
      _id: acc._id,
    })) as AccountType[],
    // Serialize platforms minimally
    platforms: form.platforms?.map((p) => ({
      TYPE: p.TYPE,
    })) as Platform[],
    postState: form.postState,
    requestType: form.requestType,
    view: form.view,
    isDirty: form.isDirty,
    requestedPost: form.requestedPost,
    accountSelectionDisabled: form.accountSelectionDisabled,
    requestFormTitle: form.requestFormTitle,
    originalPost: form.originalPost,
    originalPostState: form.originalPostState,
  };
}

/**
 * Extracts metadata from a form for the restoration UI
 */
export function extractDraftMetadata(form: PostFormState): DraftMetadata {
  const accountNames =
    form.accounts?.map((acc) => acc.login || acc.displayLogin || "Unknown") ||
    [];
  const platformNames =
    form.platforms?.map((p) => p.NAME || p.TYPE?.toString() || "Unknown") || [];

  return {
    postId: form.post._id,
    accountNames,
    platformNames,
    requestType: form.requestType || "create",
    caption: form.post.caption?.substring(0, 50), // First 50 chars
    mediaCount: form.post.mediaFiles?.length || form.post.media?.length || 0,
    timestamp: Date.now(),
  };
}

/**
 * Removes expired drafts older than 7 days
 */
export function cleanupExpiredDrafts(
  collection: DraftsCollection
): DraftsCollection {
  const now = Date.now();
  const validDrafts = collection.drafts.filter(
    (draft) => now - draft.timestamp < DRAFT_TTL_MS
  );

  return {
    ...collection,
    drafts: validDrafts,
    lastSaved: validDrafts.length > 0 ? collection.lastSaved : 0,
  };
}

/**
 * Checks localStorage quota and implements LRU eviction if needed
 * Returns true if save can proceed, false if quota exceeded even after cleanup
 */
function checkStorageQuota(): boolean {
  try {
    const testKey = "__storage_test__";
    localStorage.setItem(testKey, "test");
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    if (
      e instanceof Error &&
      (e.name === "QuotaExceededError" ||
        e.name === "NS_ERROR_DOM_QUOTA_REACHED")
    ) {
      // Implement LRU eviction - keep only 25 most recent drafts
      const collection = getDraftsCollection();
      if (!collection) return false;

      const sortedDrafts = collection.drafts
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 25);

      const updatedCollection: DraftsCollection = {
        version: 1,
        userId: collection.userId, // Preserve userId through quota cleanup
        drafts: sortedDrafts,
        lastSaved: Date.now(),
      };

      try {
        setLocalStoragePostFormDrafts(JSON.stringify(updatedCollection));
        toast.error("Storage limit reached. Oldest drafts removed.");
        return true;
      } catch (retryError) {
        console.error("Failed to save even after quota cleanup:", retryError);
        return false;
      }
    }
    throw e;
  }
}

/**
 * Retrieves the drafts collection from localStorage
 * Includes validation and error handling
 */
function getDraftsCollection(): DraftsCollection | null {
  try {
    const raw = getLocalStoragePostFormDrafts();
    if (!raw) {
      return {
        version: 1,
        drafts: [],
        lastSaved: 0,
      };
    }

    const collection: DraftsCollection = JSON.parse(raw);

    // Validate schema
    if (!collection.version || !Array.isArray(collection.drafts)) {
      throw new Error("Invalid draft schema");
    }

    // Validate each draft has required fields with detailed logging
    const validDrafts = collection.drafts.filter((draft) => {
      const isValid =
        draft.id &&
        draft.timestamp &&
        draft.formState &&
        draft.formState.post?._id &&
        draft.metadata;

      if (!isValid) {
        console.warn("[DraftStorage] Invalid draft skipped:", {
          id: draft.id,
          hasTimestamp: !!draft.timestamp,
          hasFormState: !!draft.formState,
          hasPost: !!draft.formState?.post,
          hasPostId: !!draft.formState?.post?._id,
          requestType: draft.formState?.requestType,
          hasMetadata: !!draft.metadata,
        });
      }

      return isValid;
    });

    return {
      version: collection.version,
      drafts: validDrafts,
      lastSaved: collection.lastSaved || 0,
    };
  } catch (error) {
    console.error("Failed to parse drafts from localStorage:", error);
    // Clear corrupted data
    clearLocalStoragePostFormDrafts();
    toast.error("Draft data corrupted. Drafts have been cleared.");
    return null;
  }
}

/**
 * Validates that a form has the required data for serialization
 */
function isValidFormForSave(form: PostFormState): boolean {
  return !!(form && form.post && form.post._id);
}

/**
 * Saves drafts to localStorage with debouncing and quota checking
 * @param forms - Array of PostFormState to save
 * @param userId - Optional user ID for validation (prevents cross-user contamination)
 */
export function saveDraftsToStorage(
  forms: PostFormState[],
  userId?: string
): void {
  // Debounce saves to prevent race conditions
  const now = Date.now();
  if (now - lastSaveTimestamp < SAVE_DEBOUNCE_MS) {
    return; // Skip if saved too recently
  }
  lastSaveTimestamp = now;

  try {
    // Filter to only valid forms before attempting save
    const validForms = forms.filter(isValidFormForSave);

    if (validForms.length === 0) {
      // No valid forms to save - this is not an error, just skip
      return;
    }

    // Check quota before attempting save
    if (!checkStorageQuota()) {
      console.error("localStorage quota exceeded, cannot save drafts");
      toast.error("Unable to save drafts. Storage is full.");
      return;
    }

    const collection = getDraftsCollection();
    if (!collection) {
      // getDraftsCollection already showed an error if data was corrupted
      // Don't show another error here
      console.error("Failed to get drafts collection, aborting save");
      return;
    }

    // Create draft objects from valid forms only
    const newDrafts: PostFormDraft[] = validForms.map((form) => ({
      id: form.post._id,
      timestamp: Date.now(),
      formState: serializeFormState(form),
      metadata: extractDraftMetadata(form),
    }));

    // Replace all existing drafts with current session's drafts
    // This prevents localStorage quota issues by ensuring only one save session exists
    const updatedCollection: DraftsCollection = {
      version: 1,
      userId: userId || collection.userId, // Preserve or set user ID
      drafts: newDrafts,
      lastSaved: Date.now(),
    };

    setLocalStoragePostFormDrafts(JSON.stringify(updatedCollection));
  } catch (error) {
    // Only log to console - don't show toast for every save failure
    // as this can be noisy during normal operation
    console.error("Failed to save drafts to localStorage:", error);
  }
}

/**
 * Retrieves all drafts from localStorage with TTL cleanup
 * @param userId - Optional user ID to validate drafts belong to current user
 * @returns Array of valid, non-expired drafts
 */
export function getDraftsFromStorage(userId?: string): PostFormDraft[] {
  const collection = getDraftsCollection();
  if (!collection) {
    return [];
  }

  // Validate user ID if provided - prevent cross-user contamination
  if (userId && collection.userId && collection.userId !== userId) {
    console.warn(
      `Draft user ID mismatch: expected ${userId}, found ${collection.userId}. Clearing drafts.`
    );
    clearDraftsFromStorage();
    return [];
  }

  // Clean up expired drafts
  const cleaned = cleanupExpiredDrafts(collection);

  // Save cleaned version back if drafts were removed
  if (cleaned.drafts.length !== collection.drafts.length) {
    setLocalStoragePostFormDrafts(JSON.stringify(cleaned));
  }

  return cleaned.drafts;
}

/**
 * Removes a single draft by post ID
 * @param postId - The post._id to remove
 */
export function removeDraftById(postId: string): void {
  try {
    const collection = getDraftsCollection();
    if (!collection) {
      return;
    }

    const filteredDrafts = collection.drafts.filter((d) => d.id !== postId);

    if (filteredDrafts.length === collection.drafts.length) {
      // No draft found with this ID, nothing to do
      return;
    }

    if (filteredDrafts.length === 0) {
      // Last draft removed, clear storage entirely
      clearLocalStoragePostFormDrafts();
    } else {
      const updatedCollection: DraftsCollection = {
        version: 1,
        userId: collection.userId, // Preserve userId when removing drafts
        drafts: filteredDrafts,
        lastSaved: Date.now(),
      };

      setLocalStoragePostFormDrafts(JSON.stringify(updatedCollection));
    }
  } catch (error) {
    console.error("Failed to remove draft:", error);
  }
}

/**
 * Clears all drafts from localStorage
 */
export function clearDraftsFromStorage(): void {
  clearLocalStoragePostFormDrafts();
}
